import { formatDate, formatNumber } from '@angular/common';
import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import {
  DateService, deconstructDate,
  getValue,
  interpolate,
  isDefined,
  isIsoDate,
  isIsoDateTime,
  isIsoDateTimeTz,
  isIsoDuration,
  isIsoTime,
  isIsoTimeTz, IsoDuration
} from '@softline/core';
import { LocalizationFile } from './data/localization-file';
import { SOFTLINE_CONFIG_DEFAULT_LOCALE, SOFTLINE_DATA_LOCALIZATIONS } from './l10n.shared';
import { FormatOptions } from './types/format-options';

const MUSTACHE_REGEX = /{{\s?([^{}\s]*)\s?}}/g;
const NUMBER_FORMAT_REGEX = /\d{1,3}(\.\d{1,3})?(-\d{1,3})?/;

@Injectable({ providedIn: 'root' })
export class LocalizationService {
  private defaultLocale = inject(SOFTLINE_CONFIG_DEFAULT_LOCALE);
  private readonly files = inject(SOFTLINE_DATA_LOCALIZATIONS, { optional: true }) ?? [];
  readonly locale = signal(this.defaultLocale);

  private readonly _localizationFiles = signal<LocalizationFile[]>(this.files);
  private readonly _activeFiles = computed(() => {
    const locale = this.locale();
    return this._localizationFiles().filter((o) => o.locale === locale);
  });

  readonly localizationFiles = this._localizationFiles.asReadonly();

  addFile(file: LocalizationFile): void {
    this._localizationFiles.set([...this._localizationFiles(), file]);
  }

  format(
    value: unknown,
    format?: string | null,
    options?: FormatOptions
  ): Signal<string> {
    return computed(() => {
      format = this.findFormat(format) ?? format;
      const locale = this.locale();
      switch (typeof value) {
        case 'number':
          if (!format && options?.preventNumberDefault) return '' + value;
          if (!format) return formatNumber(value, options?.locale ?? locale);
          if (options?.currency)
            return this.getCurrency(
              value,
              format,
              options?.currency,
              options?.locale ?? locale
            );
          return formatNumber(value, options?.locale ?? locale, format);
        case 'string':
          if (!format) return '' + value;
          const date = this.formatDate(value, format, options?.timeZone);
          if (date) return date;
          break;
        case 'object':
          if (!format) return JSON.stringify(value);
          return format.replace(MUSTACHE_REGEX, (substring: string, b: string) => {
            const replacement = this.getObjectReplacement(value, b);
            return isDefined(replacement) ? replacement : substring;
          });
          break;
        default:
          break;
      }
      return '' + value;
    });
  }

  private findFormat(key: string | null | undefined): string | null {
    if (!key) return null;
    const files = this._activeFiles();
    for (const file of files) {
      const format = this.findInFile(key, file);
      if (format) return format;
    }
    return null;
  }

  private findInFile(key: string, file: LocalizationFile): string | undefined {
    const parts = key.split('.');
    let formats = file.formats as any;
    let format: string | undefined;
    while (parts.length > 0) {
      const partName = parts.shift() ?? '';

      if (!formats) break;

      const part = formats[partName];

      if (typeof part === 'object') formats = part;
      else if (parts.length === 0 && typeof part === 'string') format = part;
      else break;
    }
    return format;
  }

  private getCurrency(
    value: number,
    format: string,
    currency: string,
    locale: string
  ): string {
    const currencySymbol = Intl.NumberFormat(locale, {style:'currency', currency}).formatToParts().find(part => part.type === 'currency')?.value
    const matches = format.match(NUMBER_FORMAT_REGEX);
    if(!matches || matches.length === 0)
      return `${value}`;

    let formattedValue = formatNumber(value, locale, matches[0]);
    formattedValue = format.replace(NUMBER_FORMAT_REGEX, o => formattedValue);
    return interpolate(formattedValue, {CODE: currency, SYMBOL: currencySymbol});
  }

  private formatDate(
    value: string,
    format: string,
    timeZone?: string | null
  ): string | undefined {
    if(isIsoDate(value) || isIsoDateTime(value) || isIsoDateTimeTz(value))
      return formatDate(value, format, this.locale() ?? 'de', timeZone ?? undefined);
    else if(isIsoTime(value) || isIsoTimeTz(value))
      return formatDate('1970-01-01T'+value, format, this.locale() ?? 'de', timeZone ?? undefined);
    else if (isIsoDuration(value))
      return this.formatDuration(value, format);
    return undefined;
  }

  private getObjectReplacement(value: unknown, mustache: string): string {
    const [name, format] = mustache.replace('{', '').replace('}', '').split(':');

    let replacement = getValue(value, name);
    if (!format) return replacement;

    const resolvedFormat = this.findFormat(format.replace("'", ''));
    if (!resolvedFormat) return replacement;

    switch (typeof replacement) {
      case 'string':
        const formattedDate = this.formatDate(replacement, resolvedFormat);
        if (formattedDate) replacement = formattedDate;
        break;
      case 'number':
        replacement = formatNumber(replacement, this.locale() ?? 'de', resolvedFormat);
        break;
      default:
        break;
    }
    return replacement;
  }

  private formatDuration(value: IsoDuration, format: string): string | undefined {
    const deconstructed = deconstructDate(value);
    const regex = /\b(Y)|(M)|(W)|(D)|(HH)|(H)|(mm)|(ss)|(ms)\b/g;
    return format.replace(regex, (substring: string) => {
      switch (substring) {
        case 'Y':
          return (deconstructed.years ?? 0).toString();
        case 'M':
          return (deconstructed.months ?? 0).toString();
        case 'D':
          return (deconstructed.days ?? 0).toString();
        case 'HH':
          return (deconstructed.hours ?? 0).toString().padStart(2, '0');
        case 'H':
          return (deconstructed.hours ?? 0).toString();
        case 'mm':
          return (deconstructed.minutes ?? 0).toString().padStart(2, '0');
        case 'ss':
          return (deconstructed.seconds ?? 0).toString().padStart(2, '0');
        case 'ms':
          return (deconstructed.milliseconds ?? 0).toString().padStart(2, '0');
        default:
          return substring;
      }
    });
  }
}
